import React, { useEffect, useState } from 'react';
import { Container, Image, Flex, Menu, MenuItem, Avatar, Modal, PasswordInput, TextInput, Button, Divider, Title } from '@mantine/core';
import './header.css';
import logo from '../assets/logo.png';
import profile from '../assets/profile.jpeg';
import { useNavigate } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { toast } from 'react-toastify';
import { clearAuthToken } from '../utils/auth';

// Helper to normalize any shape of profile data
function normalizeProfile(data) {
  const orgs = Array.isArray(data?.organizations) ? data.organizations : null;
  const org = orgs?.find(o => o.primary || o.current) ?? orgs?.[0];

  if (org) {
    // Google/Directory-like data
    return {
      email: data.primaryEmail ?? data.email ?? '',
      employeeId:
        data.employeeId ??
        org.employeeNumber ??
        data.externalIds?.find(x => x.type === 'organization')?.value ??
        data.id ??
        '',
      vertical:
        data.costcenter ??
        org.costcenter ??
        org?.extensionAttributes?.vertical ??
        data.vertical ??
        org.vertical ??
        data.costCenter ??
        org.costCenter ?? 
        '',
      department: org.department ?? data.department ?? '',
      jobTitle: org.title ?? data.jobTitle ?? org.role ?? '',
    };
  }

  // Flat JSON-like (manual file/fallback)
  return {
    email: data.primaryEmail ?? data.email ?? '',
    employeeId: data.employeeId ?? data.empId ?? data.id ?? '',
    vertical: data.vertical ?? data.costcenter ?? data.costCenter ?? '',
    department: data.department ?? '',
    jobTitle: data.jobTitle ?? data.title ?? data.role ?? '',
  };
}

const unauthLinks = [
  { link: '/login', label: 'Login' },
  { link: '/signup', label: 'SignUp' },
];

export default function HeaderSimple() {
  const [profileEmail,setProfileEmail]=useState(''); 
  const [profileDepartment,setProfileDepartment]=useState(''); 
  const [profileVertical,setProfileVertical]=useState(''); 
  const [profileJobTitle,setProfileJobTitle]=useState('');  
  function getTokenFromCookie() {
    const cookies = document.cookie.split(';');
    const tokenCookie = cookies.find(cookie => cookie.trim().startsWith('token='));
    if (tokenCookie) {
      return tokenCookie.split('=')[1];
    } else {
      return null;
    }
  }

  const [profileInfo, setProfileInfo] = useState([]);
  const [employeeId, setemployeeId] = useState('');
  useEffect(() => {
    const token = getTokenFromCookie();

    if (token) {
      fetch(`${process.env.REACT_APP_API_BASE_URL}/ProfileInfo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
        .then(response => response.json())
        .then(data => {
          const p = normalizeProfile(data);
          setProfileEmail(p.email);
          setemployeeId(p.employeeId);
          setProfileVertical(p.vertical);
          setProfileDepartment(p.department);
          setProfileJobTitle(p.jobTitle);
          setProfileInfo(data);
          // setProfileDepartment(data.organizations[0].department);
          // setProfileEmail(data.primaryEmail);
          // setProfileVertical(data.organizations[0].costCenter);
          // setProfileJobTitle(data.organizations[0].title);
          // setProfileInfo(data);
        })
        .catch(error => {
          console.error('Error fetching profile info:', error);
        });
    }
  }, []);

  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [opened, { open, close }] = useDisclosure(false);
  const [changePasswordOpened, { open: openChangePassword, close: closeChangePassword }] = useDisclosure(false);
  const [profileOpened, { open: openProfile, close: closeProfile }] = useDisclosure(false);

  useEffect(() => {
    const token = getTokenFromCookie();

    if (token) {
      fetch(`${process.env.REACT_APP_API_BASE_URL}/getUser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
        .then(response => response.json())
        .then(data => {
        
          if (data.user) {
            setUserName(data.user.userName || ''); // Set to empty string if userName is null
          }
        })
        .catch(error => {
          console.error('Error fetching user:', error);
        });
    }
  }, [userName]);

  // State and validation for create user/password form
  const [createUserForm, setCreateUserForm] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    errors: {
      username: '',
      password: '',
      confirmPassword: '',
    }
  });

  const handleCreateUserChange = (e) => {
    const { name, value } = e.target;
    setCreateUserForm({
      ...createUserForm,
      [name]: value,
      errors: {
        ...createUserForm.errors,
        [name]: name === 'username' && value.trim().length === 0 ? 'Username is required' : '',
        [name]: name === 'password' && value.length < 8 ? 'Password must be at least 8 characters long' : '',
        [name]: name === 'confirmPassword' && value !== createUserForm.password ? 'Passwords do not match' : '',
      }
    });
  };

  const handleCreateUserSubmit = (e) => {
    e.preventDefault();
    const { username, password, confirmPassword } = createUserForm;

    // Validate form fields
    if (username.trim().length === 0 || password.length < 8 || confirmPassword !== password) {
      setCreateUserForm({
        ...createUserForm,
        errors: {
          username: username.trim().length === 0 ? 'Username is required' : '',
          password: password.length < 8 ? 'Password must be at least 8 characters long' : '',
          confirmPassword: confirmPassword !== password ? 'Passwords do not match' : '',
        }
      });
      return;
    }

    // Proceed with form submission logic (API calls, etc.)
    const token = getTokenFromCookie();
    if (token) {
     fetch(`${process.env.REACT_APP_API_BASE_URL}/addUserNameAndPassword`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ username, password }),
      })
        .then(response => response.json())
        .then(data => {
          if (data.message === 'Username and password added successfully') {
            close(); // Close modal after successful submission
            // Optionally, update state or show success message
            toast.success('Username and password added successfully');
          } else {
            toast.error('Failed to add username and password');
            console.error(data.error);
          }
        })
        .catch(error => {
          toast.error('Error adding username and password');
          console.error('Error adding username and password:', error);
        });
    }
  };

  // State and validation for change password form
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    errors: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    }
  });

  const handleChangePasswordChange = (e) => {
    const { name, value } = e.target;
    setChangePasswordForm({
      ...changePasswordForm,
      [name]: value,
      errors: {
        ...changePasswordForm.errors,
        [name]: name === 'currentPassword' && value.length < 8 ? 'Password must be at least 8 characters long' : '',
        [name]: name === 'newPassword' && value.length < 8 ? 'Password must be at least 8 characters long' : '',
        [name]: name === 'confirmNewPassword' && value !== changePasswordForm.newPassword ? 'Passwords do not match' : '',
      }
    });
  };

  const handleChangePasswordSubmit = (e) => {
    e.preventDefault();
    const { currentPassword, newPassword, confirmNewPassword } = changePasswordForm;

    // Validate form fields
    if (currentPassword.length < 8 || newPassword.length < 8 || confirmNewPassword !== newPassword) {
      setChangePasswordForm({
        ...changePasswordForm,
        errors: {
          currentPassword: currentPassword.length < 8 ? 'Password must be at least 8 characters long' : '',
          newPassword: newPassword.length < 8 ? 'Password must be at least 8 characters long' : '',
          confirmNewPassword: confirmNewPassword !== newPassword ? 'Passwords do not match' : '',
        }
      });
      return;
    }

    // Proceed with form submission logic (API calls, etc.)
    const token = getTokenFromCookie();
    if (token) {
      fetch(`${process.env.REACT_APP_API_BASE_URL}/changePassword`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      })
        .then(response => response.json())
        .then(data => {
          if (data.message === 'Password changed successfully') {
            closeChangePassword(); // Close modal after successful submission
            // Optionally, update state or show success message
            toast.success('Password changed successfully');
          } else {
            toast.error('Failed to change password');
            console.error(data.error);
          }
        })
        .catch(error => {
          console.error('Error changing password:', error);
        });
    }
  };

const handleMenuClick = () => {
  const isLocalhost = window.location.hostname === 'localhost';

  clearAuthToken();
  document.cookie = `token=; Path=/; Domain=${isLocalhost ? 'localhost' : '.navriti.com'}; Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=None; Secure`;

  navigate('/login');
};


  const links = [
    { link: openProfile, label: 'Profile' }, // New profile link
    { link: userName ? openChangePassword : open, label: userName ? 'Change Password' : 'Create Username and Password' },
    { link: handleMenuClick, label: 'Logout' },
  ];

 return (
    <div>
      <div style={{ margin: '0', padding: '0', position: 'relative', zIndex: 120 }}>
        <Container m={0} p='sm' h={70} bg={'white'} size={'xl'} fluid styles={{
          root: {
            borderBottom: '1px solid black',
            zIndex: 10000, // Ensure this is below the dropdown
          }
        }}>
          <Flex direction="row" align="center" justify="space-between" h="100%">
            <Flex align="center" >
              <a href='/'><Image src={logo} height={50} width={50} /></a>
              <Title order={3} fw={600} c="#1a1a1a" size={25}>
                Forms
              </Title>
            </Flex>
            <Flex align="center" position="relative">
              <Menu
                control={<Avatar size="sm" src="https://via.placeholder.com/150" />} // Placeholder image
                transition="pop"
                transitionDuration={150}
                transitionTimingFunction="ease"
                offset={22}
              >
                <Menu.Target>
                  <Image src={profile} height={50} width={50} alt='Profile'/>
                </Menu.Target>
                <Menu.Dropdown  styles={{
                  dropdown: {
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    boxShadow:
                      '0 8px 25px rgba(0, 0, 0, 0.15), 0 0 10px rgba(0, 150, 255, 0.2)',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    padding: '8px',
                    zIndex: 9999,
                  },
                  item: {
                    borderRadius: '8px',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: '#f0f7ff',
                      color: '#0077ff',
                    },
                  },
                }}>
                  {links.map((item, index) => (
                    <Menu.Item key={index} onClick={item.link}>
                      {item.label}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            </Flex>
          </Flex>
        </Container>


        <Modal opened={opened} onClose={close} 
                title={<span style={{ fontSize: '1.25rem', fontWeight: 600 }}>Create Username and Password</span>}
                centered
                overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
              }}>
          <Divider my="xs" size="sm"/>
          <form onSubmit={handleCreateUserSubmit}>
            <TextInput
              mt="sm"
              label="Username"
              placeholder="Username"
              name="username"
              value={createUserForm.username}
              onChange={handleCreateUserChange}
              error={createUserForm.errors.username}
            />
            <PasswordInput
              label="Password"
              placeholder="Password"
              name="password"
              value={createUserForm.password}
              onChange={handleCreateUserChange}
              error={createUserForm.errors.password}
            />
            <PasswordInput
              label="Confirm Password"
              placeholder="Confirm Password"
              name="confirmPassword"
              value={createUserForm.confirmPassword}
              onChange={handleCreateUserChange}
              error={createUserForm.errors.confirmPassword}
            />

            <Button type="submit" mt="sm">
              Submit
            </Button>
          </form>
        </Modal>

        <Modal opened={changePasswordOpened} onClose={closeChangePassword} 
                title={<span style={{ fontSize: '1.25rem', fontWeight: 600 }}>Change Password</span>} 
                centered
                overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
              }}>
          <Divider my="xs" size="sm"/>
          <form onSubmit={handleChangePasswordSubmit}>
            <PasswordInput
              mt="sm"
              label="Current Password"
              placeholder="Current Password"
              name="currentPassword"
              value={changePasswordForm.currentPassword}
              onChange={handleChangePasswordChange}
              error={changePasswordForm.errors.currentPassword}
            />
            <PasswordInput
              label="New Password"
              placeholder="New Password"
              name="newPassword"
              value={changePasswordForm.newPassword}
              onChange={handleChangePasswordChange}
              error={changePasswordForm.errors.newPassword}
            />
            <PasswordInput
              label="Confirm New Password"
              placeholder="Confirm New Password"
              name="confirmNewPassword"
              value={changePasswordForm.confirmNewPassword}
              onChange={handleChangePasswordChange}
              error={changePasswordForm.errors.confirmNewPassword}
            />
            <Button type="submit" mt="sm">
              Submit
            </Button>
          </form>
        </Modal>

        <Modal opened={profileOpened} onClose={closeProfile} 
                title={<span style={{ fontSize: '1.25rem', fontWeight: 600 }}>Profile Information</span>}
                centered overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
              }}>
          <Divider my="xs" size="sm"/>
          <div className="space-y-1">
            <p>
              <span style={{ fontWeight: 600 }}>Email: </span>{'  '}
              <span>{profileEmail || ''}</span>
            </p>
            <p>
              <span style={{ fontWeight: 600 }}>Employee ID: </span>{'  '}
              <span>{employeeId || ''}</span> {/* profileInfo.id */}
            </p>
            <p>
              <span style={{ fontWeight: 600 }}>Vertical: </span>{'  '}
              <span>{profileVertical || ''}</span>
            </p>
            <p>
              <span style={{ fontWeight: 600 }}>Department: </span>{'  '}
              <span>{profileDepartment || ''}</span>
            </p>
            <p>
              <span style={{ fontWeight: 600 }}>Job Title: </span>{'  '}
              <span>{profileJobTitle || ''}</span>
            </p>
          </div>
        </Modal>
      </div>
    </div>
  );
}
